import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
	faCloudArrowDown,
	faCopy,
	faGlobePointer,
	faPaperPlane,
	faShareNodes,
} from "@fortawesome/pro-solid-svg-icons";
import {
	Anchor,
	Button,
	Dialog,
	DialogButton,
	Form,
	Input,
	SubmitButton,
	Typography,
	openInNewTab,
	useNotifications,
	useZodForm,
} from "@tosize/ui";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocalStorage } from "usehooks-ts";
import { z } from "zod";
import { type OnSaveReturnType, useConfig } from "../context/ConfigContext";
import { useCabinetStore } from "../context/StoreContext";
import { usePrice } from "../hooks/usePrice";
import { serialize } from "./renderer/data";

export function SaveDialog() {
	const intl = useIntl();
	const price = usePrice();
	const { notify } = useNotifications();
	const { state } = useCabinetStore();
	const { wishlistButton, mode, settings, onSave, customer } = useConfig();

	const [open, onOpenChange] = useState<"leave" | "action" | false>(false);
	const [response, setResponse] = useState<OnSaveReturnType["data"] | undefined>();

	const [lastSaveTime, setLastSaveTime] = useLocalStorage("tfc-save-dialog-last-timestamp", 0);

	const form = useZodForm({
		schema: z.object({
			reference: z.string().optional(),
			name: z.string().optional(),
			email: z.string().email(),
		}),
		defaultValues: {
			name: customer?.name,
			email: customer?.email,
		},
	});

	const {
		formState: { isSubmitting },
	} = form;

	function close() {
		onOpenChange(false);
		setResponse(undefined);
	}

	useEffect(() => {
		function handleMouseLeave(event: MouseEvent) {
			if (Date.now() - lastSaveTime < 300000) {
				return;
			}

			setLastSaveTime(Date.now());

			if (event.clientY < 0 && onSave && mode === "add" && !open) {
				onOpenChange("leave");
			}
		}

		document.body.addEventListener("mouseleave", handleMouseLeave);

		return () => {
			document.body.removeEventListener("mouseleave", handleMouseLeave);
		};
	}, [lastSaveTime, open, onSave, mode, setLastSaveTime]);

	if (!onSave || mode === "view" || mode === "expandedView") {
		return null;
	}

	return (
		<>
			<div className="flex w-full items-center gap-2">
				<Button
					starticon={faShareNodes}
					variant="outlined"
					className="grow"
					onClick={() => onOpenChange((prev) => (prev ? false : "action"))}
				>
					{intl.formatMessage({ defaultMessage: "Bewaar mijn ontwerp" })}
				</Button>

				<LoadDesignButton />

				{wishlistButton}
			</div>

			<Dialog
				open={!!open}
				onOpenChange={() => {
					onOpenChange((prev) => (prev ? false : "action"));
					setResponse(undefined);
				}}
				forceOpen={isSubmitting}
				title={
					response
						? intl.formatMessage({ defaultMessage: "Jouw ontwerp direct delen?" })
						: open === "leave"
							? intl.formatMessage({ defaultMessage: "Sla het ontwerp op!" })
							: intl.formatMessage({ defaultMessage: "Jij bent de ontwerper!" })
				}
			>
				{response ? (
					<div className="space-y-4">
						<Typography>
							{intl.formatMessage({
								defaultMessage:
									"Het ontwerp is naar je gemaild. Daarnaast kan je eenvoudig je ontwerp via onderstaande link delen. Deze link is ook erg handig om mee te sturen wanneer je nog een vraag hebt voor onze klantenservice.",
							})}
						</Typography>

						<div className="scrollbar-none flex grow overflow-auto border border-shades border-dashed bg-shades-light px-4 py-2 text-shades-dark leading-none">
							<div className="scrollbar-none select-all self-center overflow-auto whitespace-nowrap">
								{response.url}
							</div>
						</div>

						<div className="flex gap-4">
							<Button
								starticon={faGlobePointer}
								variant="outlined"
								onClick={async () => {
									await navigator.clipboard.writeText(response.url);

									notify({
										variant: "info",
										title: intl.formatMessage({ defaultMessage: "Url gekopieerd" }),
										subtitle: response.url,
									});
								}}
							>
								{intl.formatMessage({ defaultMessage: "Url kopiëren" })}
							</Button>

							<Button
								starticon={faCopy}
								variant="outlined"
								onClick={async () => {
									await navigator.clipboard.writeText(response.code);
									notify({
										variant: "info",
										title: intl.formatMessage({ defaultMessage: "Code gekopieerd" }),
										subtitle: response.code,
									});
								}}
							>
								{intl.formatMessage({ defaultMessage: "Code kopiëren" })}
							</Button>

							<Anchor
								starticon={faWhatsapp}
								variant="outlined"
								target="_blank"
								href={`whatsapp://send?text=${encodeURIComponent(
									`${intl.formatMessage(
										{ defaultMessage: "Bekijk mijn ontwerp op {value}" },
										{ value: settings?.get("store-name") },
									)}: ${response.url}`,
								)}`}
							>
								{intl.formatMessage({ defaultMessage: "Deel via Whatsapp" })}
							</Anchor>
						</div>
					</div>
				) : (
					<Form
						form={form}
						className="flex flex-col gap-4"
						onSubmit={async (values) => {
							if (values) {
								const data = serialize(state);

								const response = await onSave({ data, price: price.money, ...values });

								if (!response?.data) {
									notify({
										variant: "danger",
										title: intl.formatMessage({
											defaultMessage: "Er is iets misgegaan",
										}),
										subtitle: response?.serverError,
									});

									close();
								}

								setResponse(response?.data);
							}
						}}
					>
						<Typography>
							{open === "leave"
								? intl.formatMessage({
										defaultMessage:
											"Ga je nu weg, dan wordt je ontwerp niet opgeslagen. Mail het ontwerp naar jezelf en ga op een later moment verder.",
									})
								: intl.formatMessage({
										defaultMessage:
											"We bewaren je ontwerp en sturen je nu en dan een kattebelletje totdat je zover bent om het af te maken. Voer hieronder je e-mailadres in voor akkoord.",
									})}
						</Typography>

						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:last:odd:*:col-span-full">
							<Input
								name="reference"
								label={intl.formatMessage({ defaultMessage: "Referentie" })}
								placeholder={intl.formatMessage({
									defaultMessage: "Geef je ontwerp een naam",
								})}
							/>
							<Input
								name="name"
								label={intl.formatMessage({ defaultMessage: "Naam" })}
								placeholder={intl.formatMessage({ defaultMessage: "Vul je naam in" })}
							/>
							<Input
								name="email"
								label={intl.formatMessage({ defaultMessage: "E-mailadres" })}
								placeholder={intl.formatMessage({
									defaultMessage: "Vul je e-mailadres in",
								})}
							/>
						</div>

						<SubmitButton starticon={faPaperPlane}>
							{intl.formatMessage({ defaultMessage: "Ontwerp opslaan" })}
						</SubmitButton>
					</Form>
				)}
			</Dialog>
		</>
	);
}

function LoadDesignButton() {
	const intl = useIntl();
	const { settings } = useConfig();

	const url = settings?.get("frontend/product/tosize-furniture-url");
	const form = useZodForm({ schema: z.object({ code: z.string() }) });

	return (
		<DialogButton
			content={
				<div className="flex flex-col gap-4">
					<Typography>
						{intl.formatMessage({
							defaultMessage: "Vul de code uit je email of visitekaartje in en bekijk jouw kast!",
						})}
					</Typography>
					<Form
						form={form}
						className="flex flex-col gap-4"
						onSubmit={(values) => openInNewTab(`${url}?share_type=code&share_code=${values.code}`)}
					>
						<Input
							name="code"
							placeholder={intl.formatMessage({ defaultMessage: "Vul je code in" })}
						/>

						<SubmitButton className="self-end">
							{intl.formatMessage({ defaultMessage: "Bekijk je kast" })}
						</SubmitButton>
					</Form>
				</div>
			}
			title={intl.formatMessage({ defaultMessage: "Mijn ontwerp ophalen" })}
		>
			<Button
				starticon={faCloudArrowDown}
				title={intl.formatMessage({ defaultMessage: "Mijn ontwerp ophalen" })}
				variant="outlined"
			/>
		</DialogButton>
	);
}
